import { t } from '@transifex/native';
import type { JSX } from 'react';
import { Fragment, useEffect } from 'react';

import * as styles from 'pkg/config/styles';

import * as models from 'pkg/api/models';
import { fullName } from 'pkg/api/models/account/fmt';
import useComponentDidMount from 'pkg/hooks/useComponentDidMount';
import { useCurrentAccount, useCurrentAccountWards } from 'pkg/identity';
import { useOrganizationIdentity } from 'pkg/identity/organization';
import * as routes from 'pkg/router/routes';
import { replaceState } from 'pkg/router/state';

import SectionDescription from 'components/SectionDescription';
import Avatar from 'components/avatar';
import FeatureSelect from 'components/feature_select';
import SectionTitle from 'components/section_title';
import { useStepModalContext } from 'components/step-modal';

import {
	flashErrorMessage,
	useCreateMembership,
	useJoinModalContext,
} from 'components/group/join-modal';
import { CreateWard } from 'components/group/join-modal/create-ward';
import JoinWidget from 'components/group/join-widget';
import Column from 'components/layout/column';
import Row from 'components/layout/row';

import * as css from './styles.css';

interface AccountSelectItemProps {
	account: models.account.Account;
}

function AccountSelectItem({ account }: AccountSelectItemProps): JSX.Element {
	const { setCanGoNext } = useStepModalContext();
	const joinContext = useJoinModalContext();
	const currentAccount = useCurrentAccount();

	const isSelf = account.id === currentAccount.id;

	const isActive =
		!joinContext.isCreatingAccount &&
		joinContext.targetAccount?.id === account.id;

	const select = () => {
		let role: models.membership.RoleType = null;
		if (!isSelf) {
			role = models.membership.RoleType.Player;
		}
		joinContext.setAll({
			isCreatingAccount: false,
			targetAccount: account,
			groupRole: role,
		});
		setCanGoNext(true);
	};

	let title = fullName(account);
	let subTitle = '';

	if (account.email) {
		subTitle = account.email;
	}

	if (isSelf) {
		subTitle = title;
		title = t('Joining as myself');
	}

	useComponentDidMount(() => {
		joinContext.setAll({ group: null, groupCode: null });
	});

	return (
		<FeatureSelect active={isActive} onClick={select}>
			<Row columns="40px 1fr" align="center">
				<Avatar account={account} />
				<Column spacing={styles.spacing._2}>
					<strong>{title}</strong>
					<span className={css.subtitle}>{subTitle}</span>
				</Column>
			</Row>
		</FeatureSelect>
	);
}

interface AccountSelectProps {
	showCurrentAccount?: boolean;
	filterWardsInCurrentGroup?: boolean;
	allowAccountCreate: boolean;
}

export default function AccountSelect({
	showCurrentAccount = true,
	filterWardsInCurrentGroup = false,
	allowAccountCreate,
}: AccountSelectProps): JSX.Element {
	const { withNext, setCanGoNext } = useStepModalContext();
	const currentAccount = useCurrentAccount();
	const joinContext = useJoinModalContext();
	const createMembership = useCreateMembership();
	const modalContext = useStepModalContext();

	let addableWards = useCurrentAccountWards();
	const { wards: wardUsers } = useOrganizationIdentity();
	if (filterWardsInCurrentGroup) {
		const wardUserAccountIDs = wardUsers.map((user) => user.accountId);
		addableWards = addableWards.filter(
			(account) => !wardUserAccountIDs.includes(account.id)
		);
	}

	const handleLoadedGroup = (groupInfo: models.onboardingInfo.Group) => {
		joinContext.set('connectionInfo', groupInfo);

		if (Object.keys(groupInfo).length === 0) {
			replaceState(routes.Organization.Home(groupInfo.organizationId));

			modalContext.close();
		}
	};

	useEffect(() => {
		if (joinContext.targetAccount) {
			setCanGoNext(true);
		}
	}, [joinContext.targetAccount]);

	withNext(async () => {
		const isOrganization = joinContext.connectionInfo?.isOrganization;

		if (
			joinContext.autoJoinWithGroupCode === false ||
			!joinContext.targetAccount ||
			!joinContext.groupCode ||
			(!isOrganization && !joinContext.groupRole) // if not org we need to select role
		) {
			return Promise.resolve(true);
		}

		const isSelf = joinContext.targetAccount?.id === currentAccount.id;
		const targetAccountId = !isSelf ? joinContext.targetAccount.id : null;

		const { statusCode, membership } = await createMembership(
			joinContext.groupCode,
			joinContext.groupRole,
			targetAccountId
		);

		if (statusCode >= 400 && statusCode !== 409) {
			flashErrorMessage(statusCode);
			return Promise.resolve(false);
		}

		// If the user is already a member of the group, just hide the modal
		if (statusCode === 409) {
			modalContext.close();
		}

		// @NOTE Joining an org does not return a valid membership
		if (membership && !membership.hasOwnProperty('message')) {
			joinContext.set('membership', membership);
		}

		return Promise.resolve(true);
	});

	return (
		<Column spacing={styles.spacing._5}>
			{joinContext.groupCode && (
				<JoinWidget
					badgeSize="50px"
					groupCode={joinContext.groupCode}
					onDidLoad={handleLoadedGroup}
				/>
			)}

			{addableWards?.length > 0 && (
				<Fragment>
					<SectionTitle large>{t('Who is joining?')}</SectionTitle>
					<Column spacing={styles.spacing._1}>
						<SectionTitle>{t('Your child')}</SectionTitle>
						<SectionDescription>
							{t(
								'You automatically gain access to all groups that your child joins.'
							)}
						</SectionDescription>
					</Column>

					<Column spacing={styles.spacing._4}>
						{addableWards.map((acc: models.account.Account) => (
							<AccountSelectItem key={acc.id} account={acc} />
						))}

						{allowAccountCreate && <CreateWard />}
					</Column>
				</Fragment>
			)}

			{showCurrentAccount && (
				<Fragment>
					<SectionTitle>{t('You')}</SectionTitle>
					<AccountSelectItem account={currentAccount} />
				</Fragment>
			)}

			{allowAccountCreate && addableWards?.length === 0 && <CreateWard />}
		</Column>
	);
}
